import '../../Dashboard/Dashboard.sass'

import { AnalyticsEvent, logAnalyticsEvent, standardDebouncedLogAnalyticsEvent, trackIntercomEvent } from 'utils/analytics'
import { BEIGE_300, MOBILE_VIEW_WIDTH_QUERY } from 'constants/styling/theme'
import { Box, Stack } from '@mui/material'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { OrderAssignmentStatusFilter, OwnershipScope, Params, SortBy, SortDirection } from 'constants/misc'
import { OrderListFilters, useInfiniteListOrders } from 'dataQueries/order.query'
import { useGetUserWorkspaces } from 'dataQueries'
import { RescheduleOrderModuleProvider, canOrderBeRescheduled } from 'components/common/RescheduleOrder'
import { useDebouncedMemo } from 'utils/helpers'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'


import { AssignmentDateFilters } from '../../Dashboard/DateFilters/AssignmentDateFilters.module'
import { AssignmentSortByFilters } from 'components/pages/Dashboard/Sort/AssignmentSortByFilters.module'
import { AssignmentSortDirectionFilters } from '../../Dashboard/Sort/AssignmentSortDirectionFilters.module'
import { ClearButton } from '../../Dashboard/ClearButton'
import { ClientAssignmentStatusFilters } from './ClientAssignmentStatusFilters.module'
import { ControlPanel } from 'components/pages/Dashboard/ControlPanel'
import { DealAssignmentCard } from 'components/common/DealAssignmentCard'
import { DealDTO } from 'models/deal'
import { WorkspaceSeatType } from 'models/user'
import { DealDetailPopup } from 'components/common/DealDetailPopup/DealDetailPopup'
import DynamicQueryContent from 'components/common/DynamicQueryContent/DynamicQueryContent'
import { InfiniteList } from 'components/common/InfiniteList/InfiniteList.component'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { MUIDivider } from 'components/common/MUIDivider/MUIDivider.component'
import { OwnerFilterTab } from 'components/common/OwnerFilterTab'
import { PageTransition } from 'utils/animations'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { RescheduleOrderModal } from 'components/common/RescheduleOrderModal'
import { SearchFilter } from '../../Dashboard/SearchFilter'
import { UserFilter } from 'components/common/UserFilter'
import { Userpilot } from 'userpilot'
import moment from 'moment-timezone'
import { useBkbnSearchParams } from 'utils/hooks/useBkbnSearchParams'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'
import { useWorkspacePlan } from 'utils/hooks'

/**
 * Whether the All-visuals tab should be offered at all (ISSUE-2616). Hidden only for Restricted and
 * Sponsored seats: those can never see anyone else's visuals, so the tab could not become usable for
 * them. Subscription state deliberately does not enter into it — a Full member or admin of a free
 * workspace keeps the tab and still sees it locked (`unavailable`), it just no longer links to the
 * pricing page. Exported for unit testing.
 */
export const shouldShowAllVisualsTab = (seatType: WorkspaceSeatType | undefined): boolean => {
  return seatType !== WorkspaceSeatType.RESTRICTED && seatType !== WorkspaceSeatType.SPONSORED
}

/**
 * Page listing orders of logged in client with filters
 * @example <ClientDashboardController />
 */
export const ClientDashboardController: React.FC = () => {
  const isMobileView = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)
  const { t } = useTranslation(['dashboard_client', 'dashboard'])

  const params = useBkbnSearchParams()

  const {
    nonPersonalUserWorkspaces,
    clientData,
    currentUserWorkspace,
    userWorkspacesQuery,
    userPropertyWorkspaceId,
    isWorkspaceResolved,
  } = useUserData()

  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()

  const showAllVisualsTab = shouldShowAllVisualsTab(currentUserWorkspace?.seatType)
  const workspaceScopedId = userPropertyWorkspaceId || undefined
  // Wait for workspace resolution before fetching to prevent an unscoped fetch flashing
  // other-workspace data. Once resolved with no workspace, the hook falls back to the legacy endpoint.
  const ordersQueryEnabled = isWorkspaceResolved

  const [orderPopupId, setOrderPopupId] = useState<number | null>(null)
  const [rescheduleOrderId, setRescheduleOrderId] = useState<number>()
  const ignoreCancelledLines = useFeatureFlag(FeatureFlag.ENABLE_SF_LAST_LINE_CANCEL)

  // Workspaces
  const allUserWorkspacesQuery = useGetUserWorkspaces(hasWorkspaceActiveSubscription)
  const allUserWorkspacesData = useMemo(() => allUserWorkspacesQuery.data?.workspaces, [allUserWorkspacesQuery.data])
  const allUserWorkspacesMembers = useMemo(() => allUserWorkspacesData?.map(workspace => workspace.members ?? []).flat() ?? [], [allUserWorkspacesData])

  // SEARCH DEBOUNCE HANDLING
  const search = useMemo(() => params.get(Params.SEARCH) || '', [params])
  const debouncedSearch = useDebouncedMemo(() => search, [search], 300)

  // FILTERS
  const filters = useMemo<OrderListFilters>(() => ({
    // A stale `?ownership=WORKSPACE` link would otherwise leave a hidden-tab seat with no active tab and
    // a workspace-scoped query, so ignore the param entirely once the tab is gone (ISSUE-2616).
    scope: showAllVisualsTab
      ? params.get(Params.OWNERSHIP) as OwnershipScope || OwnershipScope.INDIVIDUAL
      : OwnershipScope.INDIVIDUAL,
    status: params.get(Params.STATUS) as OrderAssignmentStatusFilter || OrderAssignmentStatusFilter.FINISHED,
    userIds: params.getArray(Params.USERS),
    search: debouncedSearch || null,
    sortDirection: params.get(Params.SORT_DIRECTION) as SortDirection || SortDirection.DESCENDING,
    creationDateStart: moment(params.get(Params.CREATION_DATE_START)),
    creationDateEnd: moment(params.get(Params.CREATION_DATE_END)),
    shootingDateStart: moment(params.get(Params.SHOOTING_DATE_START)),
    shootingDateEnd: moment(params.get(Params.SHOOTING_DATE_END)),
    sortBy: params.get(Params.SORT_BY) as SortBy || SortBy.DEFAULT,
  }), [params, debouncedSearch, showAllVisualsTab])

  const selectedUsers = useMemo(() => new Set(filters.userIds), [filters.userIds])

  const orderList = useInfiniteListOrders(
    'CLIENT_ORDER_LIST',
    filters,
    workspaceScopedId,
    ordersQueryEnabled
  )


  /* TODO:
      In future, make an actual system for these things to work like flags OR have some better data to create calculated condition for the trigger
      For now, this is kinda handled by our Intercom settings which allows a tour to be only triggered once (per user), so all the subsequent triggers will be ignored
  */
  useEffect(() => {
    if (!clientData?.firstLoginDateTime || moment(clientData.firstLoginDateTime).isAfter(moment().subtract(3, 'days'))) {
      trackIntercomEvent(AnalyticsEvent.CLIENT_FIRST_TIME_DASHBOARD)
      Userpilot.track(AnalyticsEvent.CLIENT_FIRST_TIME_DASHBOARD)
    }
  })

  const getOwnerFilterIcon = () => {
    if (!hasWorkspaceActiveSubscription) return <LockOutlinedIcon fontSize='inherit' />
    else return <PeopleAltOutlinedIcon fontSize='inherit' />
  }

  const listTitle = useMemo(() => {
    switch (filters.status) {
      case OrderAssignmentStatusFilter.FINISHED:
        return 'dashboard:finished_deals'
      case OrderAssignmentStatusFilter.ONGOING:
        return 'dashboard:ongoing_deals'
      case OrderAssignmentStatusFilter.CANCELLED:
        return 'dashboard:cancelled_deals'

      default:
        return ''
    }
  }, [filters.status])

  return (
    <PageTransition>
      <RescheduleOrderModuleProvider orderId={rescheduleOrderId}>
        <Stack
          direction="row"
          alignItems="stretch"
          sx={{
            position: 'absolute',
            top: '6.4rem',
            left: 0,
            bottom: 0,
            width: '100%',
            height: 'calc(100vh - 6.4rem)',
            overflow: 'hidden',
            background: BEIGE_300,
          }}
        >

          <Box flex="0 0 auto">
            <ControlPanel title={t('dashboard:filters')}>

              <div className='dashboard-filters'>
                <div className="ownership-tabs">
                  <OwnerFilterTab
                    active={filters.scope === OwnershipScope.INDIVIDUAL}
                    icon={<PersonOutlineIcon fontSize='inherit' />}
                    text={t('my_visuals')}
                    onClick={() => {
                      params.set(
                        [Params.OWNERSHIP, OwnershipScope.INDIVIDUAL],
                        [Params.USERS, null]
                      )
                    }}
                  />
                  {showAllVisualsTab &&
                    <Stack flexGrow={1}>
                      <OwnerFilterTab
                        active={filters.scope === OwnershipScope.WORKSPACE}
                        icon={getOwnerFilterIcon()}
                        text={t('all_visuals')}
                        unavailable={!hasWorkspaceActiveSubscription}
                        onClick={() => {
                          if (hasWorkspaceActiveSubscription) {
                            params.set([Params.OWNERSHIP, OwnershipScope.WORKSPACE])
                            logAnalyticsEvent('Use Workspace\'s Visuals Tab', {
                              userId: clientData?.id,
                              workspaceIds: nonPersonalUserWorkspaces.map(workspace => workspace.id),
                            })
                          }
                          // The switcher is not an upsell surface (ISSUE-2616) — no pricing redirect.
                        }}
                      />
                    </Stack>
                  }
                </div>

                <MUIDivider />

                <SearchFilter
                  placeholder={t('search_order')}
                  value={search}
                  onChange={(value) => {
                    standardDebouncedLogAnalyticsEvent(`${filters.scope === OwnershipScope.INDIVIDUAL ? 'My Visuals Filter - Search Bar' : 'All Visuals Filter - Search Bar'}`, {
                      userEmail: clientData?.email,
                      organizationId: clientData?.organizationId
                    })

                    params.set([Params.SEARCH, value])
                  }}
                  onClick={() => {
                    logAnalyticsEvent(`${filters.scope === OwnershipScope.INDIVIDUAL ? 'My Visuals Filter - Click Search Icon' : 'All Visuals Filter - Click Search Icon'}`, {
                      userEmail: clientData?.email,
                      organizationId: clientData?.organizationId,
                    })
                  }}
                />

                <ClientAssignmentStatusFilters
                  filters={filters}
                  workspaceScopedId={workspaceScopedId}
                  enabled={ordersQueryEnabled}
                />

                <AssignmentDateFilters filters={filters} />

                <AssignmentSortDirectionFilters filters={filters} />

                <AssignmentSortByFilters filters={filters} />

                {filters.scope === OwnershipScope.WORKSPACE && hasWorkspaceActiveSubscription &&
                  <div className="member-filter-wrap">
                    <div className='member-filter-header'>
                      <span className="filter-section-label">{t('filter_members_label')}</span>
                      {!!selectedUsers.size &&
                        <ClearButton
                          text={t('dashboard:clear_filter')}
                          onClick={() => {
                            params.set([Params.USERS, null])
                          }}
                        />
                      }
                    </div>
                    <DynamicQueryContent
                      query={allUserWorkspacesQuery}
                      showStates={['error', 'pending']}
                    >
                      <UserFilter
                        users={allUserWorkspacesMembers}
                        isExpandable
                        maxUnexpandedUserCount={isMobileView ? 5 : 12}
                        onChange={userId => {
                          const newUsers = new Set(selectedUsers)

                          if (selectedUsers.has(userId)) newUsers.delete(userId)
                          else newUsers.add(userId)

                          params.set([Params.USERS, Array.from(newUsers)])

                          logAnalyticsEvent('Member Filter', {
                            selectedMember: newUsers.size
                          })
                        }}
                        selectedUserIds={selectedUsers}
                      />
                    </DynamicQueryContent>
                  </div>
                }

              </div>
            </ControlPanel>
          </Box>

          <Stack flex="1 1 100%" alignItems="center" sx={{ overflowY: 'auto', paddingTop: '4rem' }}>
            <InfiniteList<DealDTO>
              query={orderList}
              header={(
                <div>
                  <h2>{t(listTitle)}</h2>
                  <MUIDivider />
                </div>
              )}
              renderItem={(data) => (
                <Fragment key={data.id}>
                  <DealAssignmentCard
                    data={data}
                    onMoreInfoClick={() => setOrderPopupId(data.id)}
                    onRescheduleClick={canOrderBeRescheduled(data, ignoreCancelledLines)
                      ? () => setRescheduleOrderId(data.id)
                      : undefined
                    }
                  />
                  <DealDetailPopup
                    data={data}
                    isOpen={orderPopupId === data.id}
                    onClickOutside={() => setOrderPopupId(null)}
                    onClickClose={() => setOrderPopupId(null)}
                  />
                </Fragment>
              )}
              sx={{
                boxSizing: 'border-box',
                width: '95%',
                maxWidth: '90rem',
              }}
            />
          </Stack>

          <RescheduleOrderModal
            isOpen={!!rescheduleOrderId}
            onClose={() => setRescheduleOrderId(undefined)}
          />

        </Stack>
      </RescheduleOrderModuleProvider>
    </PageTransition>
  )
}
